import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthApiService } from '../../services/auth-api.service';
import { I18nService } from '../../services/i18n.service';
import { AuthCardComponent } from '../../ui/auth-card.component';
import { EMAIL_WITH_TLD_PATTERN } from '../../utils/email-validator';

@Component({
  selector: 'auth-forgot-password',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, AuthCardComponent],
  template: `
    <auth-card [title]="i18n.t('forgot.heading')" [subtitle]="i18n.t('forgot.subtitle')">
      @if (sent()) {
        <p class="au-alert au-alert--ok">{{ i18n.t('forgot.success') }}</p>
        <a class="au-button" [routerLink]="['/', langPath, 'login']">{{ i18n.t('forgot.back') }}</a>
      } @else {
        @if (error()) { <p class="au-alert au-alert--error">{{ error() }}</p> }

        <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
          <div class="au-field">
            <label class="au-label" for="email">{{ i18n.t('forgot.email') }}</label>
            <input id="email" type="email" autocomplete="email" required
                   class="au-input"
                   [class.au-input--error]="emailInvalid()"
                   [formControl]="form.controls.email">
            @if (emailInvalid()) {
              <span class="au-error">{{ i18n.t('common.email_invalid') }}</span>
            }
          </div>
          <button type="submit" class="au-button" [disabled]="loading()">
            {{ loading() ? i18n.t('common.loading') : i18n.t('forgot.submit') }}
          </button>
        </form>

        <p class="au-links">
          <a [routerLink]="['/', langPath, 'login']">{{ i18n.t('forgot.back') }}</a>
        </p>
      }
    </auth-card>
  `,
})
export class ForgotPasswordComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private api = inject(AuthApiService);
  protected i18n = inject(I18nService);

  protected langPath: 'ru' | 'kz' = 'ru';

  readonly form = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, Validators.pattern(EMAIL_WITH_TLD_PATTERN)],
    }),
  });

  readonly loading = signal(false);
  readonly sent = signal(false);
  readonly error = signal<string>('');

  emailInvalid(): boolean {
    const c = this.form.controls.email;
    return c.invalid && (c.dirty || c.touched);
  }

  async ngOnInit(): Promise<void> {
    const langParam = this.route.snapshot.paramMap.get('lang');
    this.langPath = langParam === 'kz' ? 'kz' : 'ru';
    await this.i18n.use(I18nService.normaliseFromUrl(langParam));
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.error.set('');
    this.api.forgotPassword(this.form.value.email!).subscribe({
      next: () => { this.sent.set(true); this.loading.set(false); },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(err.error?.message || this.i18n.t('forgot.error'));
      },
    });
  }
}
